import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from "@angular/core";
import { ControlValueAccessor, FormsModule, NgControl } from "@angular/forms";
import { MatFormField, MatHint, MatInput } from "@angular/material/input";
import { asapScheduler, startWith } from "rxjs";
import { NgTemplateOutlet } from "@angular/common";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { parseNumber } from "../../utils/number-utils.service";

@Component({
  selector: "vl-input-number",
  standalone: true,
  imports: [FormsModule, MatInput, MatFormField, MatHint, NgTemplateOutlet],
  templateUrl: "./input-number.component.html",
  styleUrl: "./input-number.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    "[class]": "hostClasses()",
    "[attr.required]": "isRequired() || null",
  },
})
export class InputNumberComponent implements ControlValueAccessor {
  onChangeFn = (_val: number) => {};
  onTouchFn = () => {};

  ngControl = inject(NgControl, { optional: true, self: true });
  destroyRef = inject(DestroyRef);

  submit = output<void>();

  max = input<number>();
  min = input<number>();
  hint = input<string>();
  placeholder = input<string>();
  size = input<"small" | "large">("large");
  allowNegative = input(true);

  brColor = input<string>();
  bgColor = input<string>();
  textColor = input<string>();
  hideSubscript = input<boolean>(false);

  value = model("");
  disabled = signal(false);

  private _touched = signal(false);
  private _invalid = signal(false);

  isRequired = computed(() => {
    this._invalid(); // se met à jour si les validators changent
    if (this.ngControl) {
      return !!this.ngControl.control?.validator?.({ value: null } as any)
        ?.required;
    }
    return false;
  });

  hasError = computed(() => {
    return !this.disabled() && this._touched() && this._invalid();
  });

  hostClasses = computed(() => {
    const classes: string[] = [`vl-input-number--${this.size()}`];
    if (this.brColor()) classes.push(`br-${this.brColor()}`);
    if (this.bgColor()) classes.push(`bg-${this.bgColor()}`);
    if (this.textColor()) classes.push(`${this.textColor()}`);
    if (this.hideSubscript()) classes.push("vl-input-number--hide-subscript");
    if (this.hasError()) classes.push("vl-input-number--error");
    return classes.join(" ");
  });

  private toModel(view: string): number {
    return view ? parseNumber(view.replace(",", ".")) : null;
  }

  private toView(n: number | string): string {
    return `${n}`.replace(".", ",");
  }

  isWriteValue = false;

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
    effect(
      () => {
        const raw = this.value();
        if (this.isWriteValue) {
          return;
        }
        const last = raw?.[raw.length - 1];
        if (last === "," || last === "-") return;

        const n = this.toModel(raw);
        if (n === undefined || n === 0 || n === null) {
          this.onChangeFn(n);
        } else if (Number.isNaN(n)) {
          this.onChangeFn(undefined);
        } else {
          this.onChangeFn(n);
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit() {
    const control = this.ngControl?.control;
    if (!control) return;
    control.statusChanges
      .pipe(startWith(control.status), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this._touched.set(control.touched);
        this._invalid.set(control.invalid);
      });
  }

  writeValue(obj: number | string): void {
    this.isWriteValue = true;
    asapScheduler.schedule(() => {
      const raw = this.value();
      const last = raw?.[raw.length - 1];
      if (last === "," || last === "-") {
        this.isWriteValue = false;
        return;
      }
      if (obj === null || obj === undefined) {
        if (raw !== "") this.value.set("");
        this.isWriteValue = false;
        return;
      }
      if (Number.isNaN(obj as number)) {
        if (raw !== "") this.value.set("");
        this.isWriteValue = false;
        return;
      }

      // Si la view courante parse déjà à la même valeur numérique que obj,
      // ne pas réécrire : l'utilisateur est en train de taper une forme
      // partielle équivalente (ex: "0,0" pour 0, "1,0" pour 1) qu'on ne
      // doit pas écraser sous peine d'empêcher la saisie de décimales.
      const currentAsNumber = raw
        ? parseNumber(raw.replace(",", "."))
        : null;
      if (
        typeof obj === "number" &&
        currentAsNumber !== null &&
        !Number.isNaN(currentAsNumber) &&
        currentAsNumber === obj
      ) {
        this.isWriteValue = false;
        return;
      }

      const viewValue = this.toView(obj);
      if (viewValue !== raw) {
        this.value.set(viewValue);
      }
      this.isWriteValue = false;
    });
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchFn = () => {
      fn();
      this._touched.set(true);
      this._invalid.set(this.ngControl?.control?.invalid ?? false);
    };
  }

  setDisabledState(isDisabled: boolean): void {
    // Defer signal write to avoid NG0600 when called from an effect/computed context
    setTimeout(() => this.disabled.set(isDisabled));
  }

  private insertAtCaret(inputEl: HTMLInputElement, text: string) {
    const start = inputEl.selectionStart ?? inputEl.value.length;
    const end = inputEl.selectionEnd ?? inputEl.value.length;

    const next =
      inputEl.value.slice(0, start) + text + inputEl.value.slice(end);
    inputEl.value = next;

    const nextCaret = start + text.length;
    inputEl.setSelectionRange(nextCaret, nextCaret);

    this.value.set(next);
  }

  /**
   * Sélectionne tout le contenu du champ au focus — pattern Excel/Sheets.
   * Évite à l'utilisateur de devoir effacer manuellement la valeur par
   * défaut (typiquement `0`) avant de saisir. La première frappe remplace
   * naturellement le contenu sélectionné. Si l'utilisateur veut éditer
   * en place plutôt que remplacer, un second clic désélectionne et place
   * le curseur normalement.
   *
   * Note : `requestAnimationFrame` reporte le `select()` après que le
   * navigateur ait fini de traiter le focus — sinon Safari et certaines
   * builds de Chrome désélectionnent immédiatement (la pose du curseur
   * post-focus écrase la sélection appliquée trop tôt).
   */
  onFocus(event: FocusEvent) {
    const inputEl = event.target as HTMLInputElement | null;
    if (!inputEl) return;
    requestAnimationFrame(() => inputEl.select());
  }

  onlyNumbers(event: KeyboardEvent) {
    this.onTouchFn();

    const inputEl = event.target as HTMLInputElement | null;
    const current = this.value() ?? "";

    const isNumber = /^[0-9]$/.test(event.key);
    const allowedNav = [
      "Backspace",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
      "Delete",
      "Home",
      "End",
    ];

    const hasComma = current.includes(",");
    if (allowedNav.includes(event.key)) return;
    if (event.key === ".") {
      event.preventDefault();

      if (hasComma) return;

      if (inputEl) {
        this.insertAtCaret(inputEl, ",");
      } else {
        this.value.set(current + ",");
      }
      return;
    }
    if (event.key === ",") {
      if (hasComma) {
        event.preventDefault();
        return;
      }
      return;
    }
    if (event.key === "-") {
      if (!this.allowNegative()) {
        event.preventDefault();
        return;
      }

      const caretAtStart = (inputEl?.selectionStart ?? 0) === 0;
      const hasMinus = current.includes("-");

      if (!caretAtStart || hasMinus) {
        event.preventDefault();
        return;
      }
      return;
    }
    if (isNumber) return;

    event.preventDefault();
  }
}
